import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLE_HELP = {
  admin: "Everything except managing staff logins",
  news_editor: "Write, edit and publish news stories and media",
  match_officer: "Fixtures, live scores, match events and schedule import",
  data_manager: "Teams, players, competitions and team assignments",
};
const EMPTY = { name: "", email: "", role: "news_editor", password: "" };
const errMsg = (err) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong";
};

const StaffForm = ({ form, setForm, roles, editing, onSubmit }) => {
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="staff-form">
      <div><label className="text-sm font-medium">Full name</label>
        <Input name="name" value={form.name} onChange={set} required className="rounded-none" data-testid="staff-name-input" /></div>
      <div><label className="text-sm font-medium">Email</label>
        <Input name="email" type="email" value={form.email} onChange={set} required disabled={editing} className="rounded-none" data-testid="staff-email-input" /></div>
      <div><label className="text-sm font-medium">Role</label>
        <div className="grid gap-2 mt-1" data-testid="staff-role-options">
          {Object.entries(roles).map(([key, label]) => (
            <label key={key} className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${form.role === key ? "border-[#009E60] bg-[#009E60]/5" : "border-gray-200 hover:border-gray-400"}`}>
              <input type="radio" name="role" value={key} checked={form.role === key} onChange={set} className="mt-1 accent-[#009E60]" data-testid={`staff-role-${key}`} />
              <span><span className="font-semibold text-sm">{label}</span><span className="block text-xs text-gray-500">{ROLE_HELP[key]}</span></span>
            </label>
          ))}
        </div></div>
      <div><label className="text-sm font-medium">{editing ? "New password (leave blank to keep)" : "Temporary password (min 8 characters)"}</label>
        <Input name="password" type="text" value={form.password} onChange={set} required={!editing} minLength={8} className="rounded-none" data-testid="staff-password-input" /></div>
      <button type="submit" className="btn-abfa-primary w-full" data-testid="staff-save-btn"><span>{editing ? "Save Changes" : "Create Login"}</span></button>
    </form>
  );
};

export default function AdminStaff() {
  const [data, setData] = useState({ roles: {}, staff: null });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => axios.get(`${API}/admin/staff`, { withCredentials: true }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openForm = (s = null) => {
    setEditing(s);
    setForm(s ? { name: s.name, email: s.email, role: s.role, password: "" } : EMPTY);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/staff/${editing.user_id}`, { name: form.name, role: form.role, password: form.password || null }, { withCredentials: true });
        toast.success("Staff login updated");
      } else {
        await axios.post(`${API}/admin/staff`, form, { withCredentials: true });
        toast.success(`Login created — share the email and temporary password with ${form.name}`);
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}'s login? They will no longer be able to sign in.`)) return;
    try {
      await axios.delete(`${API}/admin/staff/${s.user_id}`, { withCredentials: true });
      toast.success("Login removed");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <AdminLayout title="Staff Logins">
      <div data-testid="admin-staff">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <p className="text-gray-500">Create logins for staff. Each person signs in at /login and only sees the sections their role allows.</p>
          <button onClick={() => openForm()} className="btn-abfa-primary whitespace-nowrap" data-testid="add-staff-btn"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Plus className="w-4 h-4" /> Add Staff</span></button>
        </div>
        <div className="bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="staff-table">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {!data.staff && <tr><td colSpan={4} className="p-8 text-center text-gray-400" data-testid="staff-loading">Loading staff…</td></tr>}
              {data.staff?.map((s) => (
                <tr key={s.user_id} className="border-t border-gray-100" data-testid={`staff-row-${s.user_id}`}>
                  <td className="p-4 font-medium">{s.name}</td>
                  <td className="p-4 text-gray-600">{s.email}</td>
                  <td className="p-4">
                    {s.is_super_admin
                      ? <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase bg-[#FFC72C] text-black"><ShieldCheck className="w-3 h-3" /> Super Admin</span>
                      : <span className="px-2 py-1 text-xs font-bold uppercase bg-gray-100 text-gray-700">{data.roles[s.role] || s.role}</span>}
                  </td>
                  <td className="p-4 text-right whitespace-nowrap">
                    {!s.is_super_admin && (<>
                      <button onClick={() => openForm(s)} className="p-2 text-gray-500 hover:text-[#009E60]" aria-label="Edit" data-testid={`edit-staff-${s.user_id}`}><Edit className="w-4 h-4" /></button>
                      <button onClick={() => remove(s)} className="p-2 text-gray-500 hover:text-red-600" aria-label="Remove" data-testid={`delete-staff-${s.user_id}`}><Trash2 className="w-4 h-4" /></button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading uppercase">{editing ? "Edit Staff Login" : "Add Staff Login"}</DialogTitle></DialogHeader>
            <StaffForm form={form} setForm={setForm} roles={data.roles} editing={!!editing} onSubmit={submit} />
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
